import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Dialog } from '../components/ui/dialog';
import { Field } from '../components/ui/field';
import { Checkbox, Input } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import { can, useMe } from '../lib/session';
import type { RoleRow, RolesResponse, StaffRow } from '../lib/types';
import { formatDateTime } from '../lib/utils';

/** Сотрудники и роли: кто что может в админке. Изменения — с повторным подтверждением. */
export function StaffPage() {
  const { t } = useTranslation();
  const me = useMe();
  return (
    <>
      <PageHeader title={t('staff.title')} description={t('staff.hint')} />
      <div className="grid gap-6">
        {can(me, 'users.manage') ? <StaffList /> : null}
        {can(me, 'roles.manage') ? <RolesList /> : null}
      </div>
    </>
  );
}

const rolesQuery = {
  queryKey: ['roles'],
  queryFn: () => api<RolesResponse>('/roles'),
};

function StaffList() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const queryClient = useQueryClient();
  const staff = useQuery({
    queryKey: ['staff'],
    queryFn: () => api<{ items: StaffRow[] }>('/staff'),
  });
  const [editing, setEditing] = useState<StaffRow | 'new'>();
  const [error, setError] = useState<string>();

  const toggle = async (row: StaffRow) => {
    setError(undefined);
    try {
      await api(`/staff/${row.id}/${row.isActive ? 'disable' : 'enable'}`, { method: 'POST' });
      await queryClient.invalidateQueries({ queryKey: ['staff'] });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Card>
      <CardHeader
        title={t('staff.people')}
        actions={
          <Button
            size="sm"
            onClick={() => {
              setEditing('new');
            }}
          >
            {t('staff.add')}
          </Button>
        }
      />
      <CardContent className="text-sm">
        {error ? (
          <Alert variant="destructive" className="mb-3">
            {error}
          </Alert>
        ) : null}
        <table className="w-full">
          <tbody>
            {(staff.data?.items ?? []).map((s) => (
              <tr key={s.id} className="border-b last:border-0">
                <td className="py-2">
                  <div className="font-medium">{s.displayName}</div>
                  <div className="text-xs text-muted-foreground">{s.email}</div>
                </td>
                <td>
                  <span className="flex flex-wrap gap-1">
                    {s.roles.map((r) => (
                      <Badge key={r} variant="outline">
                        {r}
                      </Badge>
                    ))}
                  </span>
                </td>
                <td>
                  {s.twofa ? null : <Badge variant="warning">{t('staff.no2fa')}</Badge>}
                  {s.isActive ? null : <Badge variant="secondary">{t('common.inactive')}</Badge>}
                </td>
                <td className="text-xs text-muted-foreground">
                  {s.lastLoginAt ? formatDateTime(s.lastLoginAt, i18n.language) : '—'}
                </td>
                <td className="text-right">
                  <span className="flex justify-end gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditing(s);
                      }}
                    >
                      {t('staff.roles')}
                    </Button>
                    {s.id !== me.userId ? (
                      <Button size="sm" variant="outline" onClick={() => void toggle(s)}>
                        {s.isActive ? t('staff.disable') : t('staff.enable')}
                      </Button>
                    ) : null}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
      {editing ? (
        <StaffDialog
          staff={editing === 'new' ? undefined : editing}
          onClose={() => {
            setEditing(undefined);
          }}
        />
      ) : null}
    </Card>
  );
}

function StaffDialog({ staff, onClose }: { staff?: StaffRow; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const roles = useQuery(rolesQuery);
  const [selected, setSelected] = useState<string[]>(staff?.roles ?? []);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState<string>();
  const [error, setError] = useState<string>();

  const save = async () => {
    setError(undefined);
    try {
      if (staff) {
        await api(`/staff/${staff.id}/roles`, { method: 'PUT', body: { roles: selected } });
        onClose();
      } else {
        const created = await api<{ temporaryPassword?: string }>('/staff', {
          method: 'POST',
          body: { email: email.trim(), displayName: name.trim(), roles: selected },
        });
        setPassword(created.temporaryPassword ?? '');
      }
      await queryClient.invalidateQueries({ queryKey: ['staff'] });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={staff ? staff.displayName : t('staff.add')}
      footer={
        password === undefined ? (
          <Button
            disabled={selected.length === 0 || (!staff && (!email.includes('@') || !name.trim()))}
            onClick={() => void save()}
          >
            {t('common.save')}
          </Button>
        ) : null
      }
    >
      <div className="grid gap-3 text-sm">
        {password !== undefined ? (
          <Alert variant="success">
            {t('staff.created')} <code className="font-mono">{password}</code>
          </Alert>
        ) : (
          <>
            {!staff ? (
              <>
                <Field label="Email">
                  {(id) => (
                    <Input
                      id={id}
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                      }}
                    />
                  )}
                </Field>
                <Field label={t('staff.name')}>
                  {(id) => (
                    <Input
                      id={id}
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                      }}
                    />
                  )}
                </Field>
              </>
            ) : null}
            <p className="font-medium">{t('staff.roles')}</p>
            {(roles.data?.roles ?? []).map((r) => (
              <label key={r.id} className="flex items-center gap-2">
                <Checkbox
                  checked={selected.includes(r.code)}
                  onChange={(e) => {
                    setSelected((s) =>
                      e.target.checked ? [...s, r.code] : s.filter((c) => c !== r.code),
                    );
                  }}
                />
                {r.name} <span className="text-xs text-muted-foreground">{r.code}</span>
              </label>
            ))}
          </>
        )}
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </div>
    </Dialog>
  );
}

function RolesList() {
  const { t } = useTranslation();
  const roles = useQuery(rolesQuery);
  const [editing, setEditing] = useState<RoleRow>();
  return (
    <Card>
      <CardHeader title={t('staff.rolesTitle')} description={t('staff.rolesHint')} />
      <CardContent className="grid gap-2 text-sm">
        {(roles.data?.roles ?? []).map((r) => (
          <button
            key={r.id}
            type="button"
            disabled={r.code === 'owner'}
            onClick={() => {
              setEditing(r);
            }}
            className="flex items-center justify-between rounded-md border p-3 text-left hover:bg-accent disabled:opacity-70"
          >
            <span>
              <span className="font-medium">{r.name}</span>{' '}
              <span className="text-xs text-muted-foreground">{r.code}</span>
            </span>
            <span className="text-muted-foreground">
              {r.code === 'owner'
                ? t('staff.allPermissions')
                : t('staff.permissionsCount', { count: r.permissions.length })}
            </span>
          </button>
        ))}
      </CardContent>
      {editing && roles.data ? (
        <RoleDialog
          role={editing}
          all={roles.data.permissions}
          onClose={() => {
            setEditing(undefined);
          }}
        />
      ) : null}
    </Card>
  );
}

function RoleDialog({
  role,
  all,
  onClose,
}: {
  role: RoleRow;
  all: RolesResponse['permissions'];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string[]>(role.permissions);
  const [error, setError] = useState<string>();
  const groups = [...new Set(all.map((p) => p.module))];
  const save = async () => {
    setError(undefined);
    try {
      await api(`/roles/${role.id}/permissions`, {
        method: 'PUT',
        body: { permissions: selected },
      });
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={role.name}
      className="max-w-2xl"
      footer={<Button onClick={() => void save()}>{t('common.save')}</Button>}
    >
      <div className="grid gap-3 text-sm">
        {groups.map((g) => (
          <fieldset key={g} className="grid gap-1">
            <legend className="font-medium">{g}</legend>
            {all
              .filter((p) => p.module === g)
              .map((p) => (
                <label key={p.code} className="flex items-center gap-2">
                  <Checkbox
                    checked={selected.includes(p.code)}
                    onChange={(e) => {
                      setSelected((s) =>
                        e.target.checked ? [...s, p.code] : s.filter((c) => c !== p.code),
                      );
                    }}
                  />
                  <span className="font-mono text-xs">{p.code}</span> — {p.description}
                </label>
              ))}
          </fieldset>
        ))}
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </div>
    </Dialog>
  );
}
